import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  IconButton,
  CircularProgress,
  Checkbox,
  TextField,
  useMediaQuery,
} from '@material-ui/core';
import {
  CheckCircle,
  Cancel,
  Visibility,
  CheckCircleOutline,
  CancelOutlined,
} from '@material-ui/icons';
import { toast } from 'react-toastify';
import MaterialTable from 'material-table';

// Internal Dependencies
import { connect } from 'react-redux';
import { updateModalContent, toggleModal } from '../../actions/modal';
import Colours from '../../styles/colours';
import MText from '../common/MText';
import MButton from '../common/MButton';
import apiService from '../../services.js/apiService';
import { IsArray, IsEmpty } from '../../common/checks';
import { Status } from '../../common/constants';
import useDebounce from '../global/use-debounce';
import PhotoDetailsView from '../photoDetailsView';
import ProgressiveImage from '../common/ProgressiveImage';
import convert from '../../common/convert';

const PhotosMgmt = (props) => {
  const isMobile = useMediaQuery('(max-width:600px)');
  const [photos, setPhotos] = useState([]);
  const [selected, setSelected] = useState([]);
  const [filters, setFilters] = useState({
    poiId:   '',
    photoId: '',
    status:  Status.Pending,
    sortBy:  'updatedAt',
  });
  const debouncedFilters = useDebounce(filters, 750);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [pagination, setPagination] = useState({
    pageNo:   0,
    pageSize: 10,
  });

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [activePhotoId, setActivePhotoId] = useState(null);
  const fetchPhotos = async () => {
    try {
      setLoading(true);

      const params = {
        ...debouncedFilters,
        status:   debouncedFilters.status === 'All' ? '' : debouncedFilters.status,
        pageNo:   pagination.pageNo,
        pageSize: pagination.pageSize,
      };

      const result = await apiService.getPhotos(params);
      const photoData = IsArray(result.rows) ? result.rows : [];
      setPhotos(photoData);
      setTotalCount(result.totalCount || 0);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPhotos();
  }, [debouncedFilters, pagination.pageNo, pagination.pageSize]);

  const handleFilterChange = (key, value) => {
    setPagination((prev) => ({ ...prev, pageNo: 0 }));
    setFilters((prev) => ({ ...prev, [key]: value }));
    setSelected([]);
  };

  const handlePageChange = (page) => {
    setPagination((prev) => ({ ...prev, pageNo: page }));
  };

  const handleRowsPerPageChange = (pageSize) => {
    setPagination({ pageNo: 0, pageSize });
  };

  const bulkProcessPhotos = async (photoIdsToProcess, status) => {
    try {
      setLoading(true);

      await apiService.bulkProcessPhotos({
        photoIds: photoIdsToProcess,
        status,
      });

      const action = status === Status.Approved ? Status.Approved : Status.Rejected;

      toast.success(`${action} ${photoIdsToProcess.length} photo(s) successfully`);

      setSelected([]);
      await fetchPhotos();
    } catch (err) {
      console.error(`Bulk ${status.toLowerCase()} error:`, err);
      toast.error(err.message || `Failed to ${status.toLowerCase()} photos`);
    } finally {
      setLoading(false);
    }
  };

  const handleBulkProcess = async (status) => {
    try {
      let photoIdsToProcess = [];

      if (selected.length > 0) {
        photoIdsToProcess = selected;
      } else {
        const pendingPhotoIds = photos
          .filter((photo) => photo.status === Status.Pending)
          .map((photo) => photo.id);
        photoIdsToProcess = pendingPhotoIds;
      }

      const action = status === Status.Approved ? 'Approve' : 'Reject';
      if (photoIdsToProcess.length === 0) {
        toast.error(`No pending photos to ${action}`);
        return;
      }

      // Show confirmation modal if no photos are selected
      if (IsEmpty(selected)) {
        props.updateModalContent({
          title:   `Confirm ${action} All`,
          content: (
            <div className="p-3">
              <MText
                text={`All ${photoIdsToProcess.length} photo(s) will be ${action === 'Approve' ? 'approved' : 'rejected'}. Proceed?`}
              />
            </div>
          ),
          actionButton: {
            actionText: 'Confirm',
            actionType: 'primary',
            action:     async () => {
              await bulkProcessPhotos(photoIdsToProcess, status);
            },
          },
          confirmText:      'Proceed',
          cancelText:       'Cancel',
          closeAfterAction: true,
          centered:         true,
        });

        props.toggleModal();
      } else {
        // Directly process the photos if there are selections
        await bulkProcessPhotos(photoIdsToProcess, status);
      }
    } catch (err) {
      console.error(`Bulk ${status.toLowerCase()} error:`, err);
      toast.error(err.message || `Failed to ${status.toLowerCase()} photos`);
    } finally {
      setLoading(false);
    }
  };

  const handleBulkApprove = () => handleBulkProcess(Status.Approved);
  const handleBulkReject = () => handleBulkProcess(Status.Rejected);

  const handleSelectAllCurrentPage = (checked) => {
    if (!checked) {
      const currentPageIds = photos.map((p) => p.id);
      setSelected((prev) => prev.filter((id) => !currentPageIds.includes(id)));
      return;
    }

    const pendingIds = photos
      .filter((p) => p.status === Status.Pending)
      .map((p) => p.id);

    setSelected((prev) => Array.from(new Set([...prev, ...pendingIds])));
  };

  const handleViewPhoto = (photo) => {
    setActivePhotoId(photo.id);
    setIsDialogOpen(true);
  };

  const handleApprove = async (id) => {
    try {
      setLoading(true);

      await apiService.bulkProcessPhotos({
        photoIds: [id],
        status:   Status.Approved,
      });

      toast.success('Photo approved successfully');
      fetchPhotos();
    } catch (err) {
      console.error('Approve error:', err);
      toast.error(err.message || 'Failed to approve photo');
    } finally {
      setLoading(false);
    }
  };

  const handleReject = async (id) => {
    try {
      setLoading(true);

      await apiService.bulkProcessPhotos({
        photoIds: [id],
        status:   'Rejected',
      });

      toast.success('Photo rejected successfully');
      fetchPhotos();
    } catch (err) {
      console.error('Reject error:', err);
      toast.error(err.message || 'Failed to reject photo');
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = (id) => {
    setSelected((prev) => (prev.includes(id)
      ? prev.filter((item) => item !== id)
      : [...prev, id]));
  };

  const pendingPhotosOnPage = photos.filter((p) => p.status === Status.Pending);
  const pendingIdsOnPage = pendingPhotosOnPage.map((p) => p.id);

  const columns = [
    {
      field:     'checkbox',
      filtering: false,
      cellStyle: {
        width: '2%',
      },
      headerStyle: {
        textAlign: 'center',
      },
      title: (
        <Checkbox
          disabled={pendingPhotosOnPage.length === 0}
          indeterminate={
            pendingIdsOnPage.some((id) => selected.includes(id))
            && !pendingIdsOnPage.every((id) => selected.includes(id))
          }
          checked={
            pendingPhotosOnPage.length > 0
            && pendingIdsOnPage.every((id) => selected.includes(id))
          }
          onChange={(e) => handleSelectAllCurrentPage(e.target.checked)}
        />
      ),
      render: (rowData) => (
        <Checkbox
          checked={selected.indexOf(rowData.id) !== -1}
          onChange={() => handleSelect(rowData.id)}
          disabled={rowData.status !== Status.Pending}
        />
      ),
    },
    {
      title:     'Photo Id',
      field:     'id',
      filtering: false,
      cellStyle: {
        width:     '10%',
        textAlign: 'center',
      },
      headerStyle: {
        textAlign: 'center',
      },
    },
    {
      title:     'Photo',
      field:     'photoUrl',
      filtering: false,
      cellStyle: {
        width:     '10%',
        textAlign: 'center',
        cursor:    'pointer',
      },
      headerStyle: {
        textAlign: 'center',
      },
      render: (rowData) => (
        <Box
          onClick={() => handleViewPhoto(rowData)}
          sx={{
            cursor:    'pointer',
            '&:hover': {
              opacity: 0.7,
            },
            transition: 'opacity 0.2s',
          }}
        >
          <ProgressiveImage
            image={convert.imageResizeUrl(rowData.photoUrl, 300, 300)}
            width={150}
            height={150}
            style={{ objectFit: 'cover', borderRadius: 4 }}
          />
        </Box>
      ),
    },
    {
      title:     'POI Id',
      field:     'poiId',
      filtering: false,
      cellStyle: {
        width:     '15%',
        textAlign: 'center',
      },
      headerStyle: {
        textAlign: 'center',
      },
      render: (rowData) => (
        <a target="_blank" href={`/admin/poi/${rowData.poiId}`} rel="noreferrer">
          {rowData.poiId}
        </a>
      ),
    },
    {
      title:     'Comment Id',
      field:     'commentId',
      filtering: false,
      cellStyle: {
        width:     '15%',
        textAlign: 'center',
      },
      headerStyle: {
        textAlign: 'center',
      },
    },
    {
      title:     'Status',
      field:     'status',
      filtering: false,
      cellStyle: {
        width:     '15%',
        textAlign: 'center',
      },
      headerStyle: {
        textAlign: 'center',
      },
      render: (rowData) => (
        <Chip
          label={rowData.status}
          style={{
            backgroundColor: rowData.status === Status.Approved ? Colours.Green1
              : rowData.status === Status.Rejected ? Colours.Red
                : Colours.Orange,
            color: 'white',
          }}
          size="small"
        />
      ),
    },
    {
      title:     'Updated At',
      field:     'updatedAt',
      cellStyle: {
        width:     '15%',
        textAlign: 'center',
      },
      headerStyle: {
        textAlign: 'center',
      },
      render: (rowData) => (
        <>
          {new Date(rowData.updatedAt).toLocaleDateString()}
          {' '}
          {new Date(rowData.updatedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
        </>
      ),
    },
    {
      title:     'Actions',
      filtering: false,
      cellStyle: {
        width:     '15%',
        minWidth:  isMobile ? '60px' : '160px',
        textAlign: 'center',
      },
      headerStyle: {
        textAlign: 'center',
        minWidth:  isMobile ? '60px' : '160px',
      },
      render: (rowData) => (
        <Box
          display="flex"
          flexDirection={isMobile ? 'column' : 'row'}
          justifyContent="center"
          style={{ gap: 2 }}
        >
          {
            [Status.Pending, Status.Rejected].includes(rowData.status) && (
              <IconButton
                size="medium"
                style={{
                  color:   Colours.Green1,
                  padding: '8px',
                }}
                onClick={() => handleApprove(rowData.id)}
                title="Approve"
                disabled={loading}
              >
                <CheckCircle fontSize="medium" />
                {' '}
              </IconButton>
            )
          }
          {' '}
          <IconButton
            size="medium"
            className="mx-1"
            style={{
              color: Colours.Gray6,
            }}
            onClick={() => handleViewPhoto(rowData)}
            title="View Full Screen"
          >
            <Visibility fontSize="medium" />
            {' '}
          </IconButton>

          {[Status.Pending, Status.Approved].includes(rowData.status) && (
            <IconButton
              size="medium"
              style={{
                color:   Colours.Red,
                padding: '8px',
              }}
              onClick={() => handleReject(rowData.id)}
              title="Reject"
              disabled={loading}
            >
              <Cancel fontSize="medium" />
              {' '}
            </IconButton>
          )}
        </Box>
      ),
    },
  ];

  const isPendingFilter = filters.status === Status.Pending;
  const isAllFilter = filters.status === 'All';
  const hasSelection = selected.length > 0;

  const showBulkActions =  isPendingFilter || (isAllFilter && hasSelection);

  return (
    <div>
      <div className="content-header">
        <MText variant="h1" colour={Colours.Gray6} bold text="Photos Management" />
      </div>

      <Box display="flex" justifyContent="flex-start" alignItems="end" m={0} p={1} bgcolor="background.paper">

        <Box p={1} width="17%">
          <FormControl fullWidth>
            <TextField
              label="Photo id"
              type="number"
              value={filters.photoId || ''}
              onChange={(e) => handleFilterChange('photoId', e.target.value)}
              fullWidth
            />
          </FormControl>
        </Box>

        <Box p={1} width="17%">
          <FormControl fullWidth>
            {/* <InputLabel>POI Id</InputLabel> */}
            <TextField
              label="POI Id"
              type="number"
              value={filters.poiId || ''}
              onChange={(e) => handleFilterChange('poiId', e.target.value)}
              fullWidth
            />
          </FormControl>
        </Box>

        <Box p={1} width="17%">
          <FormControl fullWidth>
            <InputLabel id="status-filter-label">Status</InputLabel>
            <Select
              labelId="status-filter-label"
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
            >
              <MenuItem value="All">All</MenuItem>
              <MenuItem value={Status.Pending}>{Status.Pending}</MenuItem>
              <MenuItem value={Status.Approved}>{Status.Approved}</MenuItem>
              <MenuItem value={Status.Rejected}>{Status.Rejected}</MenuItem>
            </Select>
          </FormControl>
        </Box>

        <Box p={1} width="17%">
          <FormControl fullWidth>
            <InputLabel id="sort-filter-label">Sort By</InputLabel>
            <Select
              labelId="sort-filter-label"
              value={filters.sortBy}
              onChange={(e) => handleFilterChange('sortBy', e.target.value)}
            >
              <MenuItem value="id">Id</MenuItem>
              <MenuItem value="updatedAt">Updated At</MenuItem>
              <MenuItem value="poiId">POI Id</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Box>

      <MaterialTable
        className="material-table"
        title=""
        columns={columns}
        data={photos}
        isLoading={loading}
        page={pagination.pageNo}
        totalCount={totalCount}
        options={{
          pageSize:            pagination.pageSize,
          pageSizeOptions:     [10, 25, 50],
          actionsColumnIndex:  -1,
          emptyRowsWhenPaging: false,
          search:              false,
          sorting:             false,
          showTitle:           false,
          tableLayout:         'auto',
          headerStyle:         {
            backgroundColor: Colours.White,
            fontWeight:      'bold',
          },
          rowStyle: (rowData) => ({
            backgroundColor: selected.indexOf(rowData.id) !== -1 ? '#e3f2fd' : 'inherit',
          }),
        }}
        localization={{
          body: {
            emptyDataSourceMessage: (
              <Box sx={{ py: 4, textAlign: 'center' }}>
                <MText variant="body1" colour="textSecondary" text="No photos found" />
              </Box>
            ),
          },
        }}
        onChangePage={(page) => handlePageChange(page)}
        onChangeRowsPerPage={(pageSize) => handleRowsPerPageChange(pageSize)}
      />

      {showBulkActions && (
        <Box
          display="flex"
          justifyContent="end"
          m={0}
          p={2}
          bgcolor="background.paper"
          style={{
            backgroundColor: Colours.White,
            marginTop:       '16px',
            marginBottom:    '16px',
            borderTop:       '1px solid #e0e0e0',
          }}
        >
          <Box display="flex" alignItems="center">
            <MText
              variant="body1"
              style={{
                marginTop:    '8px',
                marginBottom: '8px',
                marginLeft:   '8px',
                color:        selected.length > 0 ? '#333' : '#666',
              }}
              size={14}
              text={selected.length > 0
                ? `${selected.length} photo(s) selected`
                : 'No photo selected'}
            />
          </Box>
          <Box display="flex" className="ml-5">
            <MButton
              colour={Colours.Green1}
              style={{
                marginRight: '14px',
                padding:     '8px 16px',
              }}
              onClick={handleBulkApprove}
              disabled={loading}
              text={selected.length === 0 ? 'Approve All' : 'Approve Selected'}
              icon={() => (
                loading
                  ? <CircularProgress size={20} style={{ color: 'white', marginRight: 8 }} />
                  : <CheckCircleOutline style={{ color: 'white', marginRight: 8 }} />
              )}
              textSize={14}
              textColour="white"
            />
            <MButton
              colour={Colours.Red}
              className="ml-5"
              style={{
                padding: '8px 16px',
              }}
              onClick={handleBulkReject}
              disabled={loading}
              text={selected.length === 0 ? 'Reject All' : 'Reject Selected'}
              icon={() => (
                loading
                  ? <CircularProgress size={20} style={{ color: 'white', marginRight: 8 }} />
                  : <CancelOutlined style={{ color: 'white', marginRight: 8 }} />
              )}
              textSize={14}
              textColour="white"
            />
          </Box>
        </Box>
      )}

      <PhotoDetailsView
        open={isDialogOpen}
        photoId={!IsEmpty(activePhotoId) ? activePhotoId : ''}
        onClose={() => {
          setIsDialogOpen(false);
          setActivePhotoId(null);
        }}
      />
    </div>
  );
};

PhotosMgmt.propTypes = {
  updateModalContent: PropTypes.func.isRequired,
  toggleModal:        PropTypes.func.isRequired,
};

const mapActionToProps = {
  updateModalContent,
  toggleModal,
};

export default connect(null, mapActionToProps)(PhotosMgmt);
